// ShowFormerToggle — the one control that brings former rows back.
//
// A former customer is one we no longer serve: retired (the relationship
// ended) or superseded (its instance was re-parented onto the customer that
// replaced it). Both are kept, not deleted, and every list that can hold one
// hides it by default: a list of the customers we serve is what an operator
// opens the screen for. This is how a reader asks for them. The service says
// how many the default hid (`former_hidden`), and the number sits on the label
// so the control never promises rows it cannot show; when nothing is former
// there is nothing to toggle, and the control is not drawn at all.
//
// An instance list's former rows are its retired instances — an instance
// cannot be superseded — so the label says "instances" there and "customers"
// on the customer lists. Same parameter, same component.
//
// Flipping it re-reads the screen with `?show_former=true`. On the staff
// screens that is a second screen-open in the audit trail, deliberately: the
// person asked to see the former rows, and the trail should say so.
//
// DS Switch (react-aria) and DS Label by module path, as every component here.
// The Switch renders a <label> of its own, so the DS Label inside it is a span.

import { Label } from '@miq/design-system/components/ui/label';
import { Switch } from '@miq/design-system/components/ui/switch';

export function ShowFormerToggle({
  showing,
  hiddenCount,
  onChange,
  of,
}: {
  /** Whether former rows are currently in the list. */
  showing: boolean;
  /**
   * How many rows the toggle governs: what the default hid, or — once they are
   * shown — how many of the rows on the screen are former.
   */
  hiddenCount: number;
  onChange: (showing: boolean) => void;
  /** What the list holds, so the label names the kind of row it brings back. */
  of: 'customers' | 'instances';
}) {
  if (!showing && hiddenCount === 0) {
    return null;
  }
  return (
    <Switch isSelected={showing} onChange={onChange} className="shrink-0 text-sm">
      <Label elementType="span" className="cursor-pointer text-muted-foreground">
        Show former {of} ({hiddenCount})
      </Label>
    </Switch>
  );
}
